import MathUtils from '../math/MathUtils.js';
import Transform from '../math/Transform.js';
import DOMUtils from '../utils/DOMUtils.js';

export default class Camera {
  constructor(options = {}) {
    // Initial state
    this.initialState = {
      angle: options.angle || 0,
      position: options.position || { x: 0, y: 0, z: 0 },
      zoomLevel: options.zoomLevel || 1,
      skewAngle: options.skewAngle || 0.3
    };

    // Current state
    this.angle = this.initialState.angle;
    this.position = {
      x: this.initialState.position.x,
      y: this.initialState.position.y || 0,
      z: this.initialState.position.z
    };
    this.zoomLevel = this.initialState.zoomLevel;
    this.skewAngle = this.initialState.skewAngle;
    
    // Constants
    this.CUBE_SIZE = options.cubeSize || 60;
    this.ROTATION_SPEED = options.rotationSpeed || 60; // degrees per second
    this.SKEW_STEP = options.skewStep || 0.5; // per second
    this.CAMERA_SPEED = options.cameraSpeed || 300; // units per second
    // Zoom limits - per-cuboid scaling fixes mobile clipping issues
    this.MIN_ZOOM = options.minZoom || 0.05;
    this.MAX_ZOOM = options.maxZoom || 100;
    this.ZOOM_STEP = options.zoomStep || 0.5; // per second

    // DOM elements
    this.cameraEl = document.getElementById('camera');
    this.worldEl = document.getElementById('world');
    
    // Viewport bounds tracking for frustum culling
    this._viewportDirty = true;
    this.viewportBounds = { halfWidth: 0, halfHeight: 0 };

    // Change tracking for scene-level render skip optimization
    this._lastState = null;
    this._stateChanged = true;
    this._rotationChanged = true;  // Rotation affects depth order (needs full sort)
    this._transformChanged = true; // Position/zoom/skew only affects transforms
    window.addEventListener('resize', () => {
      this._viewportDirty = true;
      this.updateCameraPosition();
    });
    this.updateViewportBounds();
    this.updateCameraPosition();
  }

  /**
   * Update viewport bounds based on current window size.
   * Called on resize and initial setup.
   */
  updateViewportBounds() {
    this.viewportBounds = {
      halfWidth: window.innerWidth / 2,
      halfHeight: window.innerHeight / 2
    };
    this._viewportDirty = false;
  }

  /**
   * Update camera element position to viewport center.
   * Called on resize and initial setup.
   * Uses pixel positioning instead of CSS percentages to avoid mobile transform clipping issues.
   */
  updateCameraPosition() {
    this.cameraEl.style.left = `${window.innerWidth / 2}px`;
    this.cameraEl.style.top = `${window.innerHeight / 2}px`;
  }

  /**
   * Get current viewport bounds, updating if dirty.
   * @return {Object} Viewport bounds {halfWidth, halfHeight}
   */
  getViewportBounds() {
    if (this._viewportDirty) this.updateViewportBounds();
    return this.viewportBounds;
  }
  
  reset() {
    this.angle = this.initialState.angle;
    this.position = {
      x: this.initialState.position.x,
      y: this.initialState.position.y || 0,
      z: this.initialState.position.z
    };
    this.zoomLevel = this.initialState.zoomLevel;
    this.skewAngle = this.initialState.skewAngle;
  }
  
  move(direction, deltaTime = 1/60) {
    const moveSpeed = this.CAMERA_SPEED * deltaTime / this.zoomLevel;
    // Compensate for skew compression on Z axis to get consistent screen-space speed
    // Also flip Z direction when skew is negative (viewing from below)
    const skewFactor = Math.max(0.1, Math.abs(this.skewAngle));
    const skewSign = this.skewAngle >= 0 ? 1 : -1;
    let dx = 0, dz = 0;

    if (direction === 'up') dz = skewSign * moveSpeed / skewFactor;
    if (direction === 'down') dz = -skewSign * moveSpeed / skewFactor;
    if (direction === 'left') dx = moveSpeed;
    if (direction === 'right') dx = -moveSpeed;

    const rotation = Transform.getRotationMatrix(-this.angle);
    this.position.x -= (dx * rotation.cos - dz * rotation.sin);
    this.position.z -= (dx * rotation.sin + dz * rotation.cos);
  }

  rotate(direction, deltaTime = 1/60) {
    const rotateSpeed = this.ROTATION_SPEED * deltaTime;
    
    if (direction === 'left') {
      this.angle -= rotateSpeed;
    } else if (direction === 'right') {
      this.angle += rotateSpeed;
    }
    
    // Normalize angle
    this.angle = MathUtils.normalizeAngle(this.angle);
  }
  
  zoom(direction, deltaTime = 1/60) {
    const zoomSpeed = this.ZOOM_STEP * deltaTime;

    if (direction === 'in') {
      this.zoomLevel = MathUtils.clamp(this.zoomLevel + zoomSpeed, this.MIN_ZOOM, this.MAX_ZOOM);
    } else if (direction === 'out') {
      this.zoomLevel = MathUtils.clamp(this.zoomLevel - zoomSpeed, this.MIN_ZOOM, this.MAX_ZOOM);
    }

    // Mark viewport dirty since effective frustum size changes with zoom
    this._viewportDirty = true;
  }
  
  adjustSkew(direction, deltaTime = 1/60) {
    const skewSpeed = this.SKEW_STEP * deltaTime;
    const prevSkew = this.skewAngle;

    const minSkew = this.minSkewAngle !== undefined ? this.minSkewAngle : -2;
    if (direction === 'up') {
      this.skewAngle = MathUtils.clamp(this.skewAngle + skewSpeed, minSkew, 2); // Up to 2 (true top-down view)
    } else if (direction === 'down') {
      this.skewAngle = MathUtils.clamp(this.skewAngle - skewSpeed, minSkew, 2); // Down to -2 (true bottom-up view)
    }

    // Adjust camera position when skew changes to maintain visual stability
    const deltaSkew = this.skewAngle - prevSkew;
    this.position.z += -deltaSkew * 1; // Changed y to z
  }
  
  update(deltaTime = 1/60) {
    // Apply zoom as CSS scale transform on the world element
    // This avoids per-face re-rasterization on Safari/Mac when zoom changes
    // Previously zoom was baked into per-cuboid dimensions, causing Safari to repaint large faces
    // On Android, skip CSS scale - zoom is baked into cuboid calculations to fix clipping issues
    if (this.worldEl && !DOMUtils.isAndroid()) {
      this.worldEl.style.transform = `scale(${this.zoomLevel})`;
    } else if (this.worldEl) {
      this.worldEl.style.transform = '';
    }

    // Update display elements
    const posElement = document.getElementById('pos');
    if (posElement) {
      posElement.textContent = `Position: [${this.position.x.toFixed(0)}, ${this.position.z.toFixed(0)}]`;
    }
    
    const rotElement = document.getElementById('rot');
    if (rotElement) {
      rotElement.textContent = `Rotation: ${this.angle.toFixed(1)}°`;
    }
    
    const skewElement = document.getElementById('skewValue');
    if (skewElement) {
      skewElement.textContent = `Skew: ${this.skewAngle.toFixed(2)}`;
    }
    
    const zoomElement = document.getElementById('zoomDisplay');
    if (zoomElement) {
      zoomElement.textContent = `Zoom: ${this.zoomLevel.toFixed(2)}`;
    }
  }
  
  getQuadrant() {
    return MathUtils.getQuadrant(this.angle);
  }

  /**
   * Get cached rotation matrix for current camera angle.
   * Only recalculates when angle changes.
   * @returns {{cos: number, sin: number, angle: number}}
   */
  getCachedRotation() {
    if (!this._rotationCache || this._rotationCache.angle !== this.angle) {
      const rotation = Transform.getCameraRotation(this);
      this._rotationCache = {
        angle: this.angle,
        cos: rotation.cos,
        sin: rotation.sin
      };
    }
    return this._rotationCache;
  }

  /**
   * Check if camera state has changed since last frame.
   * Call at start of frame before rendering.
   */
  checkForChanges() {
    if (!this._lastState) {
      this._stateChanged = true;
      this._rotationChanged = true;
      this._transformChanged = true;
      return;
    }

    // Rotation affects depth ordering (requires full SAT sort)
    this._rotationChanged = this.angle !== this._lastState.angle;

    // Transform changes only require updating cuboid positions (no re-sort)
    this._transformChanged =
      this.position.x !== this._lastState.x ||
      this.position.y !== this._lastState.y ||
      this.position.z !== this._lastState.z ||
      this.zoomLevel !== this._lastState.zoomLevel ||
      this.skewAngle !== this._lastState.skewAngle;

    this._stateChanged = this._rotationChanged || this._transformChanged;
  }

  /**
   * @returns {boolean} Whether camera state has changed since last clearChangeFlag()
   */
  hasChanged() {
    return this._stateChanged;
  }

  /**
   * @returns {boolean} Whether camera rotation changed (affects depth order)
   */
  hasRotationChanged() {
    return this._rotationChanged;
  }

  /**
   * @returns {boolean} Whether camera position/zoom/skew changed (transforms only)
   */
  hasTransformChanged() {
    return this._transformChanged;
  }

  /**
   * Clear change flag and snapshot current state.
   * Call at end of frame after rendering.
   */
  clearChangeFlag() {
    this._stateChanged = false;
    this._rotationChanged = false;
    this._transformChanged = false;
    this._lastState = {
      angle: this.angle,
      x: this.position.x,
      y: this.position.y,
      z: this.position.z,
      zoomLevel: this.zoomLevel,
      skewAngle: this.skewAngle
    };
  }
}